import { useNavigate, useParams } from "react-router";
import { useLocation } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import classNames from "classnames";
import { removeAuthToken } from "../../utils/helpers";
import { queryClient } from "../../App";
import { IAccessPages, IRoute, IStaffAuthTokenRequest, ApiResponse } from "../../utils/types";
import useGetHotelConfig from "../../hooks/use-get-hotel-config";

type IHeaderTypes = {
  routes: IRoute[];
  accesspages: IAccessPages[];
};

const Header = ({ routes, accesspages }: IHeaderTypes) => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: hotelConfig } = useGetHotelConfig();

  const staffAuthData: ApiResponse<IStaffAuthTokenRequest> | undefined =
    queryClient.getQueryData(["auth-user"]);
  const email = staffAuthData?.result?.data?.email ?? "";
  const departmentName = staffAuthData?.result?.data?.department?.departmentName ?? "";
  const nameParts = email.split("@")[0].split(/[._-]/);
  const displayName = nameParts
    .map((p: string) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(" ");
  const initials = nameParts
    .map((part: string) => part[0]?.toUpperCase() ?? "")
    .slice(0, 2)
    .join("");

  const NAV_ITEMS = accesspages
    .map((item) => {
      const route = routes.find((r) => r.value === item.name);
      if (!route) return null;
      return { title: route.name, path: route.path };
    })
    .filter(Boolean) as { title: string; path: string }[];

  const handleLogout = () => {
    localStorage.removeItem("token");
    removeAuthToken();
    queryClient.setQueriesData({ queryKey: ["auth-user"] }, null);
    navigate(`/${params.hotelcode}`);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="sticky top-0 z-50 shadow h-[72px] bg-white flex items-center justify-between px-6">
      <img className="h-7" src={hotelConfig?.hotelLogoBlack} alt={hotelConfig?.hotelCode} />
      <div className="flex items-center h-full gap-8">
        {NAV_ITEMS.map((item) => {
          const isActive =
            location.pathname.includes(
              `/${params.hotelcode}${item.path}`
            ) || location.pathname === `/${params.hotelcode}${item.path}`;
          return (
            <button
              key={item.path}
              onClick={() => navigate(`/${params.hotelcode}${item.path}`)}
              className={classNames(
                "h-full flex items-center text-sm font-medium border-b-2 transition-colors",
                isActive
                  ? "text-[#F41095] border-[#F41095]"
                  : "text-[#374151] border-transparent hover:text-[#F41095]"
              )}
            >
              {item.title}
            </button>
          );
        })}
      </div>

      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setDropdownOpen((prev) => !prev)}
          className="w-9 h-9 rounded-full bg-secondary text-white text-sm font-semibold flex items-center justify-center select-none focus:outline-none hover:bg-[#d40080] transition-colors"
        >
          {initials || "U"}
        </button>

        {dropdownOpen && (
          <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-[#E5E7EB] shadow-md rounded-xl z-50 overflow-hidden">
          
            <div className="px-4 py-4">
              <p className="text-sm text-primary leading-tight">{displayName}</p>
              {departmentName && (
                <p className="text-xs text-secondary font-medium mt-0.5 truncate">{departmentName}</p>
              )}
            </div>

            <div className="border-t border-[#E5E7EB]" />

         
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-[#374151] hover:bg-secondaryLight hover:text-secondary transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h6a2 2 0 012 2v1" />
              </svg>
              Logout
            </button>
          </div>
        )}
      </div>
      
    </div>
  );
};

export default Header;
